/**
 * Custom Events Tracking — subscriptions.
 *
 * subscription_click · subscription_frequency ·
 * subscription_neverrunout_click · subscription_neverrunout_frequency
 *
 * Split from assets/analytics-product.js because this is the only module that
 * has to reach into a third-party shadow root. Recharge renders the PDP purchase
 * options inside recharge-subscription-widget's shadow DOM, with the frequency
 * control a further root down in rc-selling-plans — see realTarget() for why that
 * matters. The mini bag's never-run-out controls are the theme's own markup.
 */
(function () {
  'use strict';

  var W = window;
  var A = W.WhittardAnalytics;
  if (!A) return;
  /* Refuse a second execution of this file — see assets/analytics.js. */
  if (W.__whittardAnalyticsSubscription) return;
  W.__whittardAnalyticsSubscription = true;
  var push = A.push;
  var onDomChange = A.onDomChange;

  /* ═══════════════════════════════════════════════
     Subscriptions — subscription_click, subscription_frequency

     Recharge renders the PDP purchase-type control inside a shadow root, so
     these listeners are attached through host.shadowRoot exactly as
     sections/product.liquid's own sticky-summary sync already does. The widget
     mounts asynchronously, hence the retry.
  ═══════════════════════════════════════════════ */

  var RC_HOSTS = 'recharge-subscription-widget, rc-selling-plans';
  var RC_RETRIES = 20;
  var RC_INTERVAL = 500;

  /* Recharge renders the two purchase-type radios as name="purchaseOption" with
     values "subscription" and "onetime". Mapping the value gives exactly the two
     strings the spec names, rather than the label's own text — which in this
     widget also contains the price ("Subscribe £6.95") and is lower-cased on the
     one-time option ("just this once"). */
  var RC_PURCHASE_TYPE = { subscription: 'Subscribe', onetime: 'Just this once' };

  /* Only the two purchase-type choices are recognised. The widget also holds a
     frequency control, a "learn more" button and price copy, and reporting the
     text of whatever was clicked turned every one of those into a bogus
     subscription_click with a nonsense `type`. */
  var PURCHASE_TYPE_RE = /subscribe|once|one[- ]?time|single/i;

  function purchaseTypeLabel(el) {
    var known = RC_PURCHASE_TYPE[(el.getAttribute && el.getAttribute('value')) || ''];
    if (known) return known;
    var label = el.closest('label') || el.parentElement;
    var text = (label ? label.textContent : el.textContent || '').trim();
    if (!text) return '';
    text = text.replace(/\s+/g, ' ').slice(0, 80);
    if (!PURCHASE_TYPE_RE.test(text)) return '';
    return text;
  }

  /* The real originating element, not the retargeted one.

     The frequency control lives two shadow roots deep
     (recharge-subscription-widget > rc-selling-plans), and an event crossing a
     shadow boundary is retargeted to the host — so a listener on the outer root
     sees `rc-selling-plans`, never the control inside it. composedPath() is the
     only way to see through that. */
  function realTarget(e) {
    var path = typeof e.composedPath === 'function' ? e.composedPath() : null;
    return (path && path.length ? path[0] : e.target) || null;
  }

  function bindRecharge(host) {
    if (!host.shadowRoot || host.__analyticsBound) return;
    host.__analyticsBound = true;
    var root = host.shadowRoot;

    /* Radios are read on `change`, never on `click`. Clicking a <label> that
       wraps a radio makes the browser forward a second, synthetic click to the
       input itself, so a click listener matching both reported every purchase-type
       choice twice. `change` fires once, and only when the selection actually
       moved. */
    root.addEventListener('change', function (e) {
      var el = realTarget(e);
      var radio = el && el.closest && el.closest('input[type="radio"]');
      if (!radio || !radio.checked) return;
      var label = purchaseTypeLabel(radio);
      if (label) push('subscription_click', { type: label });
    }, true);

    /* Widgets that use buttons or ARIA radios instead of real inputs. Labels and
       inputs are deliberately excluded here — they belong to the handler above. */
    root.addEventListener('click', function (e) {
      var el = realTarget(e);
      var choice = el && el.closest && el.closest('button, [role="radio"], [role="tab"]');
      if (!choice) return;
      var label = purchaseTypeLabel(choice);
      if (label) push('subscription_click', { type: label });
    }, true);

    /* Fallback only. Recharge's own "Deliver every" select is rendered
       opacity:0 / pointer-events:none and covered by the theme's listbox overlay
       (snippets/product-recharge-select.liquid), so in practice the shopper never
       touches this one — the overlay below is the real control. Kept because that
       snippet degrades to Recharge's native dropdown if its DOM ever changes. */
    root.addEventListener('mousedown', function (e) {
      var el = realTarget(e);
      if (el && el.closest && el.closest('select[name*="rc_plan"], select[name*="selling_plan"]')) {
        push('subscription_frequency');
      }
    }, true);
  }

  function watchSubscriptions() {
    /* Bound lazily wherever the widget actually mounts. Gating on the PDP buy-box
       wrapper was too narrow — if Recharge renders anywhere else, polling never
       started and none of these events fired. Polling still costs nothing on a
       page with no widget, because it only starts once a host element exists.

       An interval rather than the DOM sweep alone: attaching a shadow root is not
       a mutation of the light DOM, so the host can appear before it is bindable. */
    var polling = false;

    function bindHosts() {
      var hosts = document.querySelectorAll(RC_HOSTS);
      if (!hosts.length) return;
      hosts.forEach(bindRecharge);
      if (polling) return;
      polling = true;
      var tries = 0;
      var timer = W.setInterval(function () {
        var live = document.querySelectorAll(RC_HOSTS);
        live.forEach(bindRecharge);
        /* Stop once a shadow root is attached and bound — the retry exists to
           wait for the app to mount, not to keep running after it has. */
        if (live.length && live[0].shadowRoot) tries = RC_RETRIES;
        if (++tries >= RC_RETRIES) W.clearInterval(timer);
      }, RC_INTERVAL);
    }

    onDomChange(bindHosts);

    /* The control the shopper actually opens. product-recharge-select.liquid
       covers Recharge's invisible select with the shared custom-select listbox,
       so "Deliver every" is a light-DOM combobox whose aria-expanded is the open
       signal — exactly like the mini bag's never-run-out frequency below. It is
       built by script after load, hence the sweep. */
    onDomChange(function () {
      document.querySelectorAll('[data-recharge-frequency]').forEach(function (wrap) {
        if (wrap.__frequencyWatched) return;
        wrap.__frequencyWatched = true;
        var wasOpen = wrap.getAttribute('aria-expanded') === 'true';
        new MutationObserver(function () {
          var isOpen = wrap.getAttribute('aria-expanded') === 'true';
          if (isOpen === wasOpen) return;
          wasOpen = isOpen;
          if (isOpen) push('subscription_frequency');
        }).observe(wrap, { attributes: true, attributeFilter: ['aria-expanded'] });
      });
    });

    /* Mini bag "never run out" — the theme's own control, so no shadow root. */
    document.addEventListener('change', function (e) {
      var box = e.target.closest('[data-mini-bag-subscription-checkbox]');
      if (!box) return;
      push('subscription_neverrunout_click', { status: box.checked ? 'Tick' : 'Untick' });
    }, true);

    /* The mini bag's frequency control is not the native <select>: custom-select.js
       replaces it with a listbox and leaves the real select sr-only, tabindex="-1"
       and aria-hidden, so a pointer event never reaches it and this event never
       fired at all. The visible control is the wrapper, which carries
       role="combobox" and has its aria-expanded toggled — catching that reports
       the open however it was triggered, pointer or keyboard.

       Bound through the shared sweep because the mini bag re-renders its line
       items on every cart mutation, replacing these wrappers. */
    onDomChange(function () {
      document.querySelectorAll('[data-mini-bag-subscription-plan-wrap]').forEach(function (wrap) {
        if (wrap.__frequencyWatched) return;
        wrap.__frequencyWatched = true;
        var wasOpen = wrap.getAttribute('aria-expanded') === 'true';
        new MutationObserver(function () {
          var isOpen = wrap.getAttribute('aria-expanded') === 'true';
          if (isOpen === wasOpen) return;
          wasOpen = isOpen;
          if (isOpen) push('subscription_neverrunout_frequency');
        }).observe(wrap, { attributes: true, attributeFilter: ['aria-expanded'] });
      });
    });
  }

  /* Isolated and idempotent for the same reasons as assets/analytics.js's init. */
  var inited = false;

  function init() {
    if (inited) return;
    inited = true;
    try {
      watchSubscriptions();
    } catch (e) {
      if (W.console) W.console.warn('[analytics-subscription] failed to initialise', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
